/**
 * FormSubscriberControl
 *
 * Toolbar multi-select that adds/removes notification subscribers on the current
 * form. Rendered by `FormControl`; the write goes through
 * `updateFormSubscribers` from `UserContext`.
 *
 * Every tag collapses into one summary (Subscribed / a first name / Multiple);
 * while the dropdown is open that summary prints "Search users..." at its
 * natural width instead, and once search text is typed it renders nothing, so
 * the typed text alone fills the line. The search text is controlled and
 * cleared on every change and on close, mirroring antd's own multi-select
 * clearing, which a controlled `searchValue` would otherwise suppress.
 *
 * Reads the store through a narrow snapshot — the form id and the subscriber
 * list only — never `useFormStore()`, whose spread of `form.data` /
 * `formVersion.data` would re-render this control on every keystroke in the
 * form. `formId` is captured from the render-time snapshot before the first
 * await, so a slow write can never land on a form the user has navigated away
 * from.
 */

import { FC, useContext, useState } from 'react';
import { Select, theme } from 'antd';
import { BellOutlined, BellFilled, UserAddOutlined, DownOutlined } from '@ant-design/icons';
import { useSnapshot } from 'valtio';
import { UserContext } from '../../../providers/UserContext';
import { formStore } from '../../../store';
import { MessageContext } from '../../../providers/MessageContext';

const { useToken } = theme;

const FormSubscriberControl: FC = () => {
    const { token } = useToken();
    const [isUpdating, setIsUpdating] = useState(false);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [searchValue, setSearchValue] = useState('');

    const snap = useSnapshot(formStore.state);
    const formId = snap.formId;
    const { user, users, updateFormSubscribers } = useContext(UserContext);
    const { messageApi } = useContext(MessageContext);

    const handleChange = async (values: string[]) => {
        setSearchValue('');
        if (!formId) return;

        setIsUpdating(true);

        try {
            await updateFormSubscribers(formId, values);
            messageApi.success('Notification subscribers updated');
        } catch (err) {
            messageApi.error(`Failed to update subscribers: ${err instanceof Error ? err.message : String(err)}`);
        } finally {
            setIsUpdating(false);
        }
    };

    const currentSubscribers: readonly string[] = snap.form.data?.notificationsSubscribers ?? [];
    const isSubscribed = user?.uid ? currentSubscribers.includes(user.uid) : false;

    const quickActions = [{
        value: user?.uid ?? '',
        label: isSubscribed ? 'Unsubscribe me' : 'Subscribe me',
        title: isSubscribed ? 'Unsubscribe me' : 'Subscribe me',
        disabled: !user?.uid
    }];

    const userOptions = users
        ?.filter(u => u.docId !== user?.uid)
        .map(u => ({
            value: u.docId,
            label: `${u.name || ''} ${u.email || ''}`.trim() || u.docId,
            title: `${u.name || ''} ${u.email || ''} ${u.docId}`
        })) ?? [];

    const getSubscriberName = (userId: string) => {
        const user = users?.find(u => u.docId === userId);
        return (user?.name || user?.email || userId)?.split(/[ @.]/)[0];
    };

    return (
        <Select
            aria-label="Form notification subscribers"
            mode="multiple"
            value={[...currentSubscribers]}
            onChange={handleChange}
            loading={isUpdating}
            style={{
                width: 200,
                height: token.controlHeight,
            }}
            prefix={
                currentSubscribers.length === 0 ?
                    <BellOutlined /> :
                    <BellFilled />
            }
            suffix={<DownOutlined style={{ pointerEvents: 'none' }} />}
            styles={{
                popup: {
                    root: {
                        minWidth: 320
                    }
                }
            }}
            showSearch={{ optionFilterProp: 'title', searchValue, onSearch: setSearchValue }}
            options={[
                ...quickActions,
                ...userOptions
            ]}
            optionRender={(option) => {
                const isQuickAction = quickActions.some(q => q.value === option.value);
                if (isQuickAction) {
                    return (
                        <span style={{ color: token.colorPrimary }}>
                            {isSubscribed ? <BellFilled /> : <BellOutlined />}
                            {' '}{option.label}
                        </span>
                    );
                }
                const userItem = users?.find(u => u.docId === option.value);
                return (
                    <>
                        <UserAddOutlined /> {userItem?.name || userItem?.email?.split('@')[0] || option.value} {<span>{userItem?.email}</span>}
                    </>
                );
            }}
            placeholder={isDropdownOpen ?
                <div>
                    Search users...
                </div> :
                <div>
                    No subscribers
                </div>
            }
            onOpenChange={(open) => {
                setIsDropdownOpen(open);
                if (!open) setSearchValue('');
            }}
            maxTagCount={0}
            tagRender={(_object) => {
                const tagSelectionStyle = {
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: '100%',
                    width: '100%'
                };
                if (isDropdownOpen && searchValue) {
                    return <></>;
                }
                if (isDropdownOpen) {
                    return (
                        <div style={{ height: '100%', display: 'flex', alignItems: 'center', whiteSpace: 'nowrap', color: token.colorTextPlaceholder }}>
                            Search users...
                        </div>
                    );
                }
                if (currentSubscribers.length === 1) {
                    if (currentSubscribers[0] === user?.uid) {
                        return (
                            <div style={{ ...tagSelectionStyle }}>
                                Subscribed
                            </div>
                        );
                    }
                    return (
                        <div style={{ ...tagSelectionStyle }}>
                            {getSubscriberName(currentSubscribers[0])}
                        </div>
                    );
                }
                return (
                    <div style={{ ...tagSelectionStyle }}>
                        Multiple
                    </div>
                );
            }}
        />
    );
};

export default FormSubscriberControl; 